import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateSubContractorMutation } from '../../../../hooks/mutations/useSubContractorMutation';
import { useSubContractorsQuery } from '../../../../hooks/queries/useSubContractorsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { SubContractor } from '../../../../services/apiSLTP/materials/subContractors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateSubContractorModal from '../../../modals/admin/external/UpdateSubContractorModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface SubContractorsSettingsRowProps {
    withUsageCount: boolean;
}

const SubContractorsSettingsRow: React.FC<SubContractorsSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSubContractor, setSelectedSubContractor] = useState<SubContractor | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataSubContractors, isLoading, refetch } = useSubContractorsQuery({ queries: { withUsage: withUsageCount } });

    const { mutate: mutateSubContractor } = useUpdateSubContractorMutation({
        onSettled: () => refetch(),
        onError: (error) => console.error('Error updating subcontractor:', error.message),
    });

    const columnDefs: ColDef<SubContractor>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<SubContractor>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifySubContractor'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedSubContractor(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteSubContractor'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedSubContractor(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<SubContractor>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('email')),
            field: 'email',
            colId: 'email',
        },
        createDateTimeColumn<SubContractor>('createdAt', 'createdAt'),
        createDateTimeColumn<SubContractor>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xDailySubContractors')),
                field: 'usageCount.dailySubContractors',
                colId: 'xdailysubcontractors',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    const sortedSubContractors = useMemo(() => {
        return dataSubContractors?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [dataSubContractors]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedSubContractors.filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-subcontractors"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedSubContractor(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addSubContractor'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.SUBCONTRACTORS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateSubContractorModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    subContractor={selectedSubContractor}
                    subContractors={sortedSubContractors}
                    refetch={() => refetch()}
                />
            )}
            {showDeleteModal && selectedSubContractor && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateSubContractor({ subContractorId: selectedSubContractor._id, data: { crudStatus: crudStatusEnum.DELETED } });
                    }}
                    title={t('subContractor')}
                    itemName={selectedSubContractor.designation}
                />
            )}
        </>
    );
};

export default SubContractorsSettingsRow;
